import * as React from "react";
import { cn } from "../../lib/utils";
import {
  Eye,
  EyeOff,
  Calendar,
  MapPin,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useAddressLookup } from "../../hooks/useAddressLookup";

type InputProps = React.ComponentProps<"input"> & {
  state?: "default" | "error" | "success" | "disabled";
  text?: string;
  labelClassName?: string;
  additionalInfo?: string | false;
  helperText?: string | false;
  enableAddressLookup?: boolean;
  onAddressSelect?: (address: string) => void;
};

function Input({
  className,
  type = "text",
  state = "default",
  text,
  labelClassName,
  additionalInfo = false,
  helperText = false,
  placeholder,
  enableAddressLookup = false,
  onAddressSelect,
  onBlur,
  onChange,
  value,
  ...props
}: InputProps) {
  const [showPassword, setShowPassword] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [showSuggestions, setShowSuggestions] = React.useState(false);

  const addressLookupRef = enableAddressLookup ? inputRef : { current: null };
  const { scriptError, suggestions, isFetching, clearSuggestions } =
    useAddressLookup(addressLookupRef);

  const isPassword = type === "password";
  const isDate = type === "date";

  React.useEffect(() => {
    const inputElement = inputRef.current;
    if (type === "number" && inputElement) {
      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();
      };
      inputElement.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        inputElement.removeEventListener("wheel", handleWheel);
      };
    }
  }, [type]);

  const isAddress = enableAddressLookup;

  const stateStyles: Record<NonNullable<InputProps["state"]>, string> = {
    default:
      "border-input border-[#CECBD7] focus-visible:border-ring focus-visible:ring-ring/50",
    error:
      "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30",
    success:
      "border-green-500 focus-visible:border-green-500 focus-visible:ring-green-300",
    disabled: "bg-muted text-muted-foreground cursor-not-allowed opacity-70",
  };

  const getStateStyle = (s: NonNullable<InputProps["state"]>): string => {
    switch (s) {
      case "error":
        return stateStyles.error;
      case "success":
        return stateStyles.success;
      case "disabled":
        return stateStyles.disabled;
      default:
        return stateStyles.default;
    }
  };

  const handleSuggestionClick = (suggestion: {
    description: string;
    place_id: string;
  }) => {
    if (!inputRef.current) return;

    try {
      inputRef.current.value = suggestion.description;

      const event = new Event("input", { bubbles: true });
      Object.defineProperty(event, "target", {
        writable: false,
        value: {
          value: suggestion.description,
          name: inputRef.current.name,
        },
      });
      inputRef.current.dispatchEvent(event);

      onAddressSelect?.(suggestion.description);

      clearSuggestions();
      setShowSuggestions(false);
    } catch (error) {
      console.error("Error fetching address details:", error);
      onAddressSelect?.(suggestion.description);
      clearSuggestions();
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    onChange?.(e);
  };

  const handleCalendarClick = () => {
    if (inputRef.current && isDate) {
      inputRef.current.showPicker();
    }
  };

  return (
    <div className="w-full space-y-1 relative">
      {text && (
        <label
          className={cn(
            "block text-sm text-[#6D6584] font-normal",
            labelClassName
          )}
        >
          {text}
        </label>
      )}
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type={isPassword && showPassword ? "text" : type}
          placeholder={placeholder}
          disabled={state === "disabled"}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onChange={handleChange}
          value={value}
          className={cn(
            "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border flex h-9 w-full min-w-0 rounded-md bg-transparent px-3 py-6 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none",
            "focus-visible:ring-[3px]",
            getStateStyle(state),
            isPassword || isDate || isAddress ? "pr-10" : "",
            type === "number"
              ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              : "",
            isDate
              ? "[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-8 [&::-webkit-calendar-picker-indicator]:z-10"
              : "",
            className
          )}
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
        {isDate && (
          <button
            type="button"
            onClick={handleCalendarClick}
            className="absolute right-3 text-muted-foreground hover:text-foreground"
          >
            <Calendar className="h-4 w-4" />
          </button>
        )}

        {isAddress && (
          <div className="absolute right-3 text-muted-foreground">
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : scriptError ? (
              <AlertCircle className="h-4 w-4 text-yellow-500" />
            ) : (
              <MapPin className="h-4 w-4" />
            )}
          </div>
        )}
      </div>

      {isAddress && showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 mt-1 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              className="w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  {suggestion.description}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {isAddress && showSuggestions && isFetching && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 mt-1 p-4">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm text-gray-600">
              Searching addresses...
            </span>
          </div>
        </div>
      )}

      {scriptError && isAddress && (
        <p className="text-xs text-yellow-600 mt-1">
          Address lookup not available, but you can still type manually
        </p>
      )}

      {helperText && (
        <p
          className={cn(
            "text-xs mt-1",
            state === "error" ? "text-red-500" : "text-muted-foreground"
          )}
        >
          {helperText}
        </p>
      )}
      {additionalInfo && (
        <p className="text-xs text-muted-foreground">{additionalInfo}</p>
      )}
    </div>
  );
}

export { Input };
